import { InstagramIcon, YoutubeIcon } from '../components/icons';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { NotePencil, Aperture, Target, Check, Globe } from '../design-system/icons';
import type { UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import type { CampaignFormData } from './campaignSchema';
import { PRIMARY_GOALS, err, invalidBorder } from './constants';

interface StepBasicsProps {
  platform: CampaignFormData['platform'];
  primaryGoalVal: string;
  outreachRegion: string;
  name: string;
  brief: string;
  register: UseFormRegister<CampaignFormData>;
  setValue: UseFormSetValue<CampaignFormData>;
  errors: FieldErrors<CampaignFormData>;
}

// Figma: each step is two stacked cards — a header card, then the form card.
const CARD = 'rounded-xl border border-border bg-card px-8 py-8';

// Figma section header: 14px icon + 16px-medium label (+ required asterisk, or an
// "optional" hint when `required` is false) + 12px helper.
function FieldLabel({
  icon: Icon,
  label,
  helper,
  required = true,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  helper: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2 text-body-md-medium text-foreground">
        <Icon size={14} className="text-muted-foreground" />
        {label}
        {required ? (
          <span className="text-[14px] text-foreground-destructive-default">*</span>
        ) : (
          <span className="text-caption-regular font-normal text-muted-foreground">(optional)</span>
        )}
      </span>
      <span className="text-caption-regular text-muted-foreground">{helper}</span>
    </div>
  );
}

// Purple selected-check badge (top-right of a selected card) — Figma Brand/500.
function CheckBadge() {
  return (
    <span
      className="absolute top-3 right-3 flex size-4 items-center justify-center rounded-full bg-[var(--brand-500)] text-white"
    >
      <Check size={11} weight="bold" />
    </span>
  );
}

// X is intentionally excluded — the backend rejects it as a creatable
// campaign platform (common.CampaignCreatablePlatforms), so offering it here
// let brands fill out the entire wizard only to hit a 400 on final submit.
const REGIONS = [
  { id: 'IN' as const, label: 'India', sub: 'Asia/Kolkata · Indian holidays' },
  { id: 'US' as const, label: 'United States', sub: 'America/New_York · US holidays' },
];

const PLATFORMS = [
  { id: 'instagram' as const, label: 'Instagram', sub: 'Reels · Stories · Posts', Icon: InstagramIcon },
  { id: 'youtube' as const, label: 'YouTube', sub: 'Videos · Shorts', Icon: YoutubeIcon },
];

export function StepBasics({ platform, primaryGoalVal, outreachRegion, name, brief, register, setValue, errors }: StepBasicsProps) {
  const nameCount = name.length;
  const briefCount = brief.length;
  return (
    <div className="flex flex-col gap-4">
      {/* Header card (Figma: own card · 40px icon box · H5 title · sm helper) */}
      <div className={`${CARD} flex items-start gap-3.5`}>
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted">
          <NotePencil size={20} className="text-foreground" />
        </span>
        <div>
          <h2 className="m-0 text-heading-h5 text-foreground">Campaign basics</h2>
          <p className="m-0 text-body-sm-regular text-muted-foreground">
            Name it, pick a platform, and choose what you want to achieve.
          </p>
        </div>
      </div>

      {/* Form card — Name, Target Platform, Primary goal */}
      <div className={`${CARD} flex flex-col gap-6`}>
        {/* Campaign name */}
        <div className="flex flex-col gap-4 border-b border-border pb-6">
          <FieldLabel icon={NotePencil} label="Campaign name" helper="Used by you and your team to find this later." />
          <Input type="text" placeholder="e.g. Summer Glow — Aug 2025" maxLength={200} aria-invalid={!!errors.name} {...register('name')} className={`h-10 ${invalidBorder(!!errors.name)}`} />
          <div className="flex items-center justify-between">
            {errors.name ? (
              <span className={err}>{errors.name.message}</span>
            ) : (
              <span className="text-caption-regular text-muted-foreground">
                Tip: include the brand, product, and month for easy filtering.
              </span>
            )}
            <span className={`ml-auto shrink-0 text-caption-regular ${nameCount > 190 ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {nameCount} / 200
            </span>
          </div>
        </div>

        {/* Campaign description — maps to the campaign's top-level `description`
            (Details tab). Optional: left blank, the submit handler falls back
            to a placeholder ("Pending brief details.") rather than blocking. */}
        <div className="flex flex-col gap-4 border-b border-border pb-6">
          <FieldLabel
            icon={NotePencil}
            label="Campaign description"
            helper="A short summary of the campaign — shown on the campaign's Details tab."
            required={false}
          />
          <Textarea
            className={`min-h-[96px] resize-y leading-relaxed ${invalidBorder(!!errors.brief)}`}
            rows={3}
            maxLength={8000}
            aria-invalid={!!errors.brief}
            placeholder="e.g. Driving awareness for our summer collection launch across India."
            {...register('brief')}
          />
          <div className="flex items-center justify-between">
            {errors.brief && <span className={err}>{errors.brief.message}</span>}
            <span className={`ml-auto shrink-0 text-caption-regular ${briefCount > 7600 ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {briefCount.toLocaleString()} / 8,000
            </span>
          </div>
        </div>

        {/* Target Platform (Figma: 260px cards, 72px tall, horizontal, 40px logo box) */}
        <div className="flex flex-col gap-4 border-b border-border pb-6">
          <FieldLabel icon={Aperture} label="Target Platform" helper="Where will this campaign run? Choose one." />
          {/* flex, not a stretching grid — cards stay at their fixed Figma
              width (260px) regardless of how many platforms are offered,
              instead of stretching to fill the row when X was removed. */}
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.map(({ id, label, sub, Icon }) => {
              const isActive = platform === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    // Deliverable types are platform-scoped — a platform
                    // switch invalidates the Step 3 selection.
                    if (platform !== id) setValue('deliverables', []);
                    setValue('platform', id, { shouldValidate: true });
                  }}
                  className={`relative flex h-[72px] w-[260px] shrink-0 cursor-pointer items-center gap-3 rounded-[14px] border-[1.5px] px-4 py-3.5 text-left transition-colors ${isActive ? 'border-foreground bg-stone-50' : 'border-border bg-card hover:border-foreground/30'}`}
                >
                  {isActive && <CheckBadge />}
                  <span
                    className={`flex size-10 shrink-0 items-center justify-center rounded-[11px] ${isActive ? 'bg-stone-200' : 'bg-muted'}`}
                  >
                    <Icon size={26} />
                  </span>
                  <span className="flex min-w-0 flex-col gap-px">
                    <span className="text-body-sm-medium text-foreground">{label}</span>
                    <span className="truncate text-caption-regular text-muted-foreground">{sub}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {errors.platform && <span className={err}>{errors.platform.message}</span>}
        </div>

        {/* Outreach region */}
        <div className="flex flex-col gap-4 border-b border-border pb-6">
          <FieldLabel icon={Globe} label="Region" helper="Sets the default send time zone, business days and holidays for outreach on this campaign." />
          <div className="flex flex-wrap gap-2.5">
            {REGIONS.map(({ id, label, sub }) => {
              const isActive = outreachRegion === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setValue('outreachRegion', id, { shouldValidate: true })}
                  className={`relative flex min-h-[72px] w-[260px] shrink-0 cursor-pointer flex-col justify-center gap-px rounded-[14px] border-[1.5px] px-4 py-3.5 text-left transition-colors ${isActive ? 'border-foreground bg-stone-50' : 'border-border bg-card hover:border-foreground/30'}`}
                >
                  {isActive && <CheckBadge />}
                  <span className="text-body-sm-medium text-foreground">{label}</span>
                  <span className="truncate text-caption-regular text-muted-foreground">{sub}</span>
                </button>
              );
            })}
          </div>
          {errors.outreachRegion && <span className={err}>{errors.outreachRegion.message}</span>}
        </div>

        {/* Primary goal (Figma: 3-col horizontal choice cards, 72px tall, 36px icon box) */}
        <div className="flex flex-col gap-4">
          <FieldLabel icon={Target} label="Primary goal" helper="Discovr tailors creator matching and the brief to this." />
          <div className="grid grid-cols-3 gap-2.5">
            {PRIMARY_GOALS.map((g) => {
              const IconComp = g.icon;
              const isActive = primaryGoalVal === g.value;
              return (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setValue('primaryGoal', g.value as CampaignFormData['primaryGoal'], { shouldValidate: true })}
                  className={`relative flex min-h-[72px] cursor-pointer items-start gap-3 rounded-[12px] border-[1.5px] p-4 text-left transition-colors ${isActive ? 'border-foreground bg-stone-50' : 'border-border bg-card hover:border-foreground/30'}`}
                >
                  {isActive && <CheckBadge />}
                  <span
                    className={`flex size-9 shrink-0 items-center justify-center rounded-[10px] ${isActive ? 'bg-stone-200' : 'bg-muted'}`}
                  >
                    <IconComp size={20} className="text-foreground" weight="duotone" />
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="block text-body-sm-medium text-foreground">{g.label}</span>
                    <span className="block text-caption-regular text-muted-foreground">{g.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {errors.primaryGoal && <span className={err}>{errors.primaryGoal.message}</span>}
        </div>
      </div>
    </div>
  );
}
